
import React, { useState } from 'react';
import { Typography, Grid, Box, Button, Divider, Container } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import DirectionsIcon from '@mui/icons-material/Directions';
import { styled } from '@mui/system';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import { useTranslation } from 'react-i18next';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import Seo from '../components/seo/Seo';


const SectionTitle = ({ children }) => (
  <Typography variant="h6" sx={{ marginBottom: '0.5rem', textTransform: 'uppercase' }}>
    {children}
  </Typography>
);

const ContactBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  marginBottom: '0.5rem',
});

const ContactText = styled(Typography)({
  textAlign: 'left',
});

const Contact = () => { 
  const [showEmail, setShowEmail] = useState(false);

  const { t } = useTranslation('contact');
  const title = `Betonoki - ${t('contact')}`;
  const description =
    t('metaDescription', {
      defaultValue: 'Kontakt do Betonoki. Dane firmy, adres biura i dane kontaktowe.',
    });

  const emailStart = 'kontakt';
  const emailEnd = '@betonoki.pl'; 
  const addressLabel = 'ul. Fabryczna 28, 42-600 Tarnowskie Góry';
  const directionsUrl =
    'https://www.google.com/maps/dir/?api=1&destination=50.45528471372055,18.86594520936825&travelmode=driving';
  const mapEmbedUrl =
    'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d838.8332900608561!2d18.86594520936825!3d50.45528471372055!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47112bec20a7d815%3A0x6b069f681b1bd853!2sBetonoki%20biuro%20projektowe!5e1!3m2!1spl!2spl!4v1778012486504!5m2!1spl!2spl';

  const handleShowEmail = () => {
    setShowEmail(true);
  };
  return (
    <>
    <Seo title={title} description={description} path="/contact" image="/img/contact/bg_contact.jpg" />
    <Container maxWidth="lg">
      <Typography variant="h1" sx={{ marginBottom: '2rem' }}>
        {t('contact')}
      </Typography>
      <Box
        sx={{
          width: '100%',
          height: { xs: 220, sm: 300, md: 360 },
          backgroundImage: 'url(/img/contact/bg_contact.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          mb: 4,
        }}
      />

      <Grid container spacing={4}>
        <Grid item xs={12} md={5}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Box sx={{ marginBottom: '2rem', width: 150, textAlign: 'center' }}>
              <img src="/img/logo.png" alt="Logo" style={{ width: '150px', height: 'auto' }} />
            </Box>

            <Box sx={{ marginBottom: '1.5rem', width: 150, textAlign: 'left' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                {t('designOffice')}
              </Typography>
            </Box>

            <Divider sx={{ width: 150, mb: 2 }} />

            <Box sx={{ marginBottom: '1.5rem', width: '100%' }}>
              <SectionTitle>{t('companyData')}:</SectionTitle>
              <Typography sx={{ mb: 1 }}>BETONOKI SP. Z O.O.</Typography>

              <ContactBox sx={{ alignItems: 'flex-start' }}>
                <PlaceOutlinedIcon color="primary" sx={{ marginRight: '0.5rem', mt: '2px' }} />
                <Box>
                  <Typography sx={{ mb: 1 }}>
                    ul. Fabryczna 28
                    <br />
                    42-600 Tarnowskie Góry
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DirectionsIcon />}
                    component="a"
                    href={directionsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ textTransform: 'none', display: { xs: 'inline-flex', md: 'none' } }}
                    aria-label={`${t('navigateTo')} ${addressLabel}`}
                  >
                    {t('navigate')}
                  </Button>
                </Box>
              </ContactBox>

              <ContactBox sx={{ alignItems: 'flex-start' }}>
                <BusinessIcon color="primary" sx={{ marginRight: '0.5rem', mt: '2px' }} />
                <Typography>
                  NIP: 9691667130
                  <br />
                  KRS: 0001123324
                  <br />
                  REGON: 529459192
                </Typography>
              </ContactBox>
            </Box>

            <Box sx={{ marginBottom: '1.5rem', width: '100%' }}>
              <SectionTitle>{t('contactData')}:</SectionTitle>
              <ContactBox>
                <MailOutlineIcon sx={{ fontSize: '1.2rem', marginRight: '0.5rem' }} />
                {showEmail ? (
                  <a
                    href={`mailto:${emailStart}${emailEnd}`}
                    style={{ textDecoration: 'none', color: 'inherit' }}
                  >
                    <ContactText>{`${emailStart}${emailEnd}`}</ContactText>
                  </a>
                ) : (
                  <ContactText onClick={handleShowEmail}>
                    {`${emailStart}@[${t`show`}]`}
                  </ContactText>
                )}
              </ContactBox>
            </Box>

            <Box sx={{ width: '100%' }}>
              <SectionTitle>{t('socialLabel')}:</SectionTitle>
              <ContactBox
                component="a"
                href="https://www.linkedin.com/company/betonoki/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ textDecoration: 'none', color: 'inherit' }}
              >
                <LinkedInIcon sx={{ fontSize: '1.8rem', marginRight: '0.5rem' }} />
                <ContactText>linkedin.com/company/betonoki</ContactText>
              </ContactBox>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={7}>
          <Box
            sx={{
              width: '100%',
              height: { xs: 280, sm: 360, md: 520 },
              borderRadius: 2,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <iframe
              title="Betonoki – mapa"
              src={mapEmbedUrl}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </Box>
        </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default Contact;
